/**
 * Editar el precio de un producto que ya existe. El camino corto, y el que se
 * usa casi siempre.
 *
 * POR QUE EXISTE UN EDITOR CORTO
 * ------------------------------
 * La ficha completa pregunta QUE ES el producto (nombre, marca, familia,
 * proveedor, codigos), COMO SE FORMA su precio, COMO SE REPONE y DONDE SE
 * PUBLICA. Son cuatro decisiones y las cuatro hacen falta el dia que el
 * producto se crea.
 *
 * Despues de ese dia, casi nunca. Un iPhone del lineup ya tiene nombre, marca y
 * familia decididos por el modelo, y lo unico que se vuelve a tocar —cada vez
 * que se mueve la lista del proveedor o el dolar— es el costo y el precio. Que
 * para cambiar un numero haya que pasar por cuatro secciones y dos pantallas no
 * hace el cambio mas seguro: hace que se postergue.
 *
 * Asi que la edicion abre aca: costo, markup, precio, y el resultado abajo. La
 * ficha entera sigue a un click, en «Editar todos los datos», y no se pierde
 * nada de lo ya escrito al pasar de una a la otra — es el mismo formulario.
 *
 * Lo que NO esta aca y es a proposito: la moneda de venta y el redondeo. Las
 * dos se eligen una vez, en el alta, y despues quedan. Viven en la ficha
 * completa, plegadas.
 *
 * LA UNA EXCEPCION: EL COLOR DE UN EQUIPO
 * ---------------------------------------
 * No es un precio, pero es el unico dato de la ficha que se completa DESPUES de
 * que el producto existe. La fila del 17 Pro se carga sin color —viene en todos,
 * y por eso es la lista de precios del modelo—, y el dia que entra la unidad hay
 * que decir de cual es. Sin esto, ese dato quedaba dos clicks adentro, atras de
 * «Editar todos los datos», y la unica forma que encontraba el que atiende era
 * escribirlo en el nombre.
 *
 * El modelo y la memoria NO estan: cambiarlos no es corregir esta ficha, es otro
 * producto. Eso vive en la ficha completa.
 */
import { SlidersHorizontal } from "lucide-react";

import { SelectorDeColor } from "@/components/iphones/FichaDelEquipo";
import { InputImporte } from "@/components/money";
import { Button } from "@/components/ui/button";

import { Casilla, CampoCantidad } from "./Campos";
import { SimuladorPrecio } from "./SimuladorPrecio";
import {
  ayudaDelPrecioEscrito,
  fijarElPrecio,
  precioEnElCampo,
  precioEscritoA,
  type FormularioProducto,
  type Simulacion,
} from "./logica";

export interface EditorPrecioProps {
  form: FormularioProducto;
  /** Un solo campo. */
  cambiar: <K extends keyof FormularioProducto>(campo: K, valor: FormularioProducto[K]) => void;
  /** Varios de una, para las reglas que tocan mas de un campo a la vez. */
  parchear: (patch: Partial<FormularioProducto>) => void;
  simulacion: Simulacion;
  cotizacion: number;
  /** Abre la ficha completa, con lo ya escrito. */
  onEditarTodo: () => void;
}

export function EditorPrecio({
  form,
  cambiar,
  parchear,
  simulacion,
  cotizacion,
  onEditarTodo,
}: EditorPrecioProps): JSX.Element {
  return (
    <div className="space-y-4">
      <InputImporte
        label="Costo de reposición"
        valor={form.costo}
        onValorChange={(v) => cambiar("costo", v)}
        moneda={form.costoMoneda}
        onMonedaChange={(m) => cambiar("costoMoneda", m)}
        cotizacion={cotizacion}
        // Sin autoFocus: ⌘K abre este editor para consultar, y con el foco
        // en el costo un número tipeado de más caía ahí.
      />

      {/* El markup se edita con el tipo que el producto ya tiene. Cambiar de
          porcentaje a monto fijo es rehacer la regla del precio, no retocarla:
          eso vive en la ficha completa. */}
      {form.markupTipo === "PORCENTAJE" ? (
        <CampoCantidad
          label="Porcentaje sobre el costo"
          valor={form.markupValor}
          onValorChange={(v) => cambiar("markupValor", v)}
          sufijo="%"
          requerido
        />
      ) : (
        <InputImporte
          label="Monto fijo sobre el costo"
          valor={form.markupValor}
          onValorChange={(v) => cambiar("markupValor", v)}
          moneda={form.markupMoneda}
          onMonedaChange={(m) => cambiar("markupMoneda", m)}
          cotizacion={cotizacion}
          requerido
        />
      )}

      {/* Las dos puntas de la misma cuenta: se puede entrar por el markup o por
          el precio. Escribir el precio despeja el markup que lo da, y el
          producto sigue repriciandose. */}
      <div className="space-y-2 border-t border-border pt-4">
        <InputImporte
          label="O escribí el precio de venta"
          valor={precioEnElCampo(form, simulacion)}
          onValorChange={(v) => parchear(precioEscritoA(form, v, cotizacion))}
          // Sin `onMonedaChange`: el precio va SI o SI en la moneda de venta
          // del producto. Un precio en otra moneda no significa nada.
          moneda={form.precioMoneda}
          ayuda={ayudaDelPrecioEscrito(form, simulacion)}
        />

        <Casilla
          label="Fijar este precio"
          ayuda="No cambia cuando cambia el costo o el dólar. Para un precio que no se tiene que mover."
          marcado={form.usaPrecioManual}
          onMarcadoChange={(v) => parchear(fijarElPrecio(form, v, cotizacion))}
        />
      </div>

      {/* Un usado que llega a este editor es un precio de referencia, que no
          tiene color: los equipos se editan con su asistente. */}
      {form.esEquipo && !form.esUsado ? (
        <div className="border-t border-border pt-4">
          <SelectorDeColor
            modelo={form.modelo}
            color={form.color}
            onChange={(color) => cambiar("color", color)}
            admiteTodos
            permiteEscribir
            ayuda="«Todos» es la fila de lista de precios: el modelo viene en todos."
          />
        </div>
      ) : null}

      <SimuladorPrecio form={form} simulacion={simulacion} cotizacion={cotizacion} />

      {/* La salida a la ficha entera. Ghost y abajo: esta siempre disponible y
          no compite con Guardar, que es lo que se viene a hacer. */}
      <Button type="button" variant="ghost" size="sm" onClick={onEditarTodo}>
        <SlidersHorizontal aria-hidden="true" />
        Editar todos los datos
      </Button>
    </div>
  );
}
